import React, { RefObject, useEffect, useRef, useState } from "react";
import Keyboard from "react-simple-keyboard";

const OnScreenKeyboard = (props: {
  visible: boolean;
  onBlur: () => void;
  onChange: (input: string) => void;
  inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement>;
  hideOnEnter?: boolean;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const keyboardRef = useRef(null);

  const [layoutName, setLayoutName] = useState("default");

  // Keep the Keyboard in-sync with what may already be typed into the input
  useEffect(() => {
    if (keyboardRef.current && props.inputRef.current) {
      // @ts-ignore
      keyboardRef.current.setInput(props.inputRef.current.value);
    }
  }, [keyboardRef, props.inputRef.current?.value, props.visible]);

  useEffect(() => {
    // Handle clicking outside the keyboard, unless the user clicks back to the specified Input field
    // It is up to the parent component to decide if the keyboard should be hidden via the onBlur() prop
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        props.inputRef.current &&
        // @ts-ignore
        !wrapperRef.current.contains(event.target) &&
        // @ts-ignore
        !props.inputRef.current.contains(event.target)
      ) {
        props.onBlur();
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  // Handle Shift and Caps lock button
  const onKeyPress = (button: string) => {
    if (button.includes("{") && button.includes("}")) {
      handleLayoutChange(button);
    }
  };

  const handleLayoutChange = (button: string) => {
    // @ts-ignore
    const currentLayout = layoutName;
    let newLayoutName;
    if (["{shift}", "{shiftactivated}", "{default}"].indexOf(button) > -1) {
      newLayoutName = currentLayout === "default" ? "shift" : "default";
    } else if (["{alt}", "{altright}"].indexOf(button) > -1) {
      newLayoutName = currentLayout === "alt" ? "default" : "alt";
    }

    if (newLayoutName) {
      setLayoutName(newLayoutName);
    }

    if (props.hideOnEnter && button === "{enter}") {
      props.onBlur();
    }
  };

  if (!props.visible) {
    return <></>;
  }

  return (
    <div className={"pyht-on-screen-keyboard"} ref={wrapperRef}>
      <Keyboard
        theme={"hg-theme-default hg-theme-ios"}
        keyboardRef={(r) => (keyboardRef.current = r)}
        layoutName={layoutName}
        onKeyPress={onKeyPress}
        onChange={(val) => props.onChange(val)}
        layout={{
          default: [
            "q w e r t y u i o p {bksp}",
            "a s d f g h j k l {enter}",
            "{shift} z x c v b n m , .",
            "{alt} {space}",
          ],
          shift: [
            "Q W E R T Y U I O P {bksp}",
            "A S D F G H J K L {enter}",
            "{shiftactivated} Z X C V B N M , .",
            "{alt} {space}",
          ],
          alt: [
            "1 2 3 4 5 6 7 8 9 0 {bksp}",
            "@ # $ & * ( ) ' \" {enter}",
            "{shift} % - + = / ; : ! ?",
            "{default} {space}",
          ],
        }}
        display={{
          "{alt}": ".?123",
          "{shift}": "shift",
          "{shiftactivated}": "shift",
          "{enter}": "return",
          "{bksp}": "backspace",
          "{space}": " ",
          "{default}": "ABC",
        }}
      />
    </div>
  );
};

export default OnScreenKeyboard;
